/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T21:10:00+05:00
Intent: Localize notification headers (incl. "plan is now active" and "campaign updated") by title or event, in cards, the bell and live toasts; keep the reason out of the message body.
*/

/**
 * The gateway stores notification titles as English text. Known titles map to an i18n key so the
 * card header follows the panel language; anything else is shown as sent.
 */
const TITLE_KEYS: Array<[RegExp, string]> = [
  [/plan\s+has\s+been\s+updated|تم تحديث خطتك/i, 'plan-updated'],
  [/plan\s+is\s+now\s+active|plan\s+(has\s+been\s+)?activated/i, 'plan-active'],
  [/(contest|campaign)s?\s+published/i, 'campaign-published'],
  [/(contest|campaign)s?\s+updated/i, 'campaign-updated'],
  [/(contest|campaign)s?\s+approved/i, 'campaign-approved'],
  [/(contest|campaign)s?\s+rejected/i, 'campaign-rejected'],
  [/(contest|campaign)s?\s+started/i, 'campaign-started'],
  [/(contest|campaign)s?\s+ended/i, 'campaign-ended'],
  [/winners?\s+(have\s+been\s+)?announced/i, 'winner-announced'],
];

/** Gateway events with a fixed header, for titles the patterns above do not recognise. */
const EVENT_KEYS: Record<string, string> = {
  plan_overridden: 'plan-updated',
  plan_activated: 'plan-active',
  contest_published: 'campaign-published',
  contest_published_client: 'campaign-published',
  contest_updated: 'campaign-updated',
  contest_approved: 'campaign-approved',
  contest_rejected: 'campaign-rejected',
  contest_started: 'campaign-started',
  contest_ended: 'campaign-ended',
  contest_winners_announced: 'winner-announced',
  winner_announced: 'winner-announced',
};

export function notificationTitleKey(title: string, event?: string | null): string | null {
  const text = (title || '').trim();
  const hit = TITLE_KEYS.find(([pattern]) => pattern.test(text));
  const key = hit ? hit[1] : EVENT_KEYS[String(event || '').trim().toLowerCase()];
  return key ? `notifications.titles.${key}` : null;
}

/** The header of a notification in the panel language; unknown titles keep the sender's text. */
export function localizedNotificationTitle(item: any, translate: (key: string) => string): string {
  const title = item?.notification_title || item?.notification_name || '';
  const key = notificationTitleKey(item?.notification_title || '', item?.event || item?.type);
  const text = key ? translate(key) : '';
  // Until the language file loads, instant() hands the key back; the sender's text reads better.
  return text && text !== key ? text : campaignWording(title);
}

/** The product calls them campaigns now; older gateway titles still say "contest". */
export function campaignWording(text: string): string {
  return (text || '').replace(/\bcontest(s?)\b/gi, (word, plural) => {
    const base = word[0] === 'C' ? (word === word.toUpperCase() ? 'CAMPAIGN' : 'Campaign') : 'campaign';
    return base + (plural ? (base === 'CAMPAIGN' ? 'S' : 's') : '');
  });
}

const REASON_IN_MESSAGE = /[.\s]*(?:(?:rejection\s+|overriding\s+)?reason\s*:|سبب الرفض\s*:|سبب\s*:)\s*.+$/is;

/** The message text without its trailing "Reason: …", for cards that show the reason on its own. */
export function messageWithoutReason(message: string): string {
  const text = message || '';
  const stripped = text.replace(REASON_IN_MESSAGE, '').trimEnd();
  if (!stripped) return text;
  return /[.!?؟]$/.test(stripped) ? stripped : `${stripped}.`;
}
